import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // remaining milliseconds until expiry (null = permanent / no key)
  const [remainingMs, setRemainingMs] = useState(null);
  const expiryRef = useRef(null);

  const applyExpiry = useCallback((expiresAt) => {
    expiryRef.current = expiresAt || null;
    if (!expiresAt) {
      setRemainingMs(null);
      return;
    }
    setRemainingMs(new Date(expiresAt).getTime() - Date.now());
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("pp_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      applyExpiry(res.data.expires_at);
    } catch (error) {
      const detail = String(error?.response?.data?.detail || "").toLowerCase();
      const expired = error?.response?.status === 401 && (
        detail.includes("habis") ||
        detail.includes("masa aktif") ||
        detail.includes("expired")
      );
      if (expired) {
        localStorage.setItem("pp_expired", "1");
        localStorage.removeItem("pp_expired_notice_shown");
      }
      localStorage.removeItem("pp_token");
      expiryRef.current = null;
      setRemainingMs(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [applyExpiry]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // Tick every second so the displayed countdown and lock happen at the real expiry time.
  useEffect(() => {
    if (!expiryRef.current) return undefined;

    const tick = () => {
      const expiresAt = expiryRef.current;
      if (!expiresAt) return;

      const ms = new Date(expiresAt).getTime() - Date.now();
      setRemainingMs(ms);

      if (ms <= 0) {
        // Invalidate local access immediately, then let Login show the renewal-only screen.
        expiryRef.current = null;
        localStorage.removeItem("pp_token");
        localStorage.setItem("pp_expired", "1");
        localStorage.removeItem("pp_expired_notice_shown");
        setUser(null);
        window.location.assign("/login");
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [user]);

  const loginWithKey = async (key) => {
    const res = await api.post("/auth/key", { key });
    localStorage.setItem("pp_token", res.data.token);
    localStorage.removeItem("pp_expired");
    localStorage.removeItem("pp_expired_notice_shown");
    setUser({ id: res.data.id, label: res.data.label, expires_at: res.data.expires_at });
    applyExpiry(res.data.expires_at);
    return res.data;
  };

  const logout = async () => {
    const token = localStorage.getItem("pp_token");
    localStorage.removeItem("pp_token");
    localStorage.removeItem("pp_expired");
    localStorage.removeItem("pp_expired_notice_shown");
    localStorage.removeItem("pp_session_ended");
    expiryRef.current = null;
    setRemainingMs(null);
    setUser(null);

    if (token) {
      try {
        await api.post("/auth/logout");
      } catch (error) {
        console.error("Logout request failed:", error);
      }
    }

    window.location.assign("/login");
  };

  const logoutToAccount = async () => {
    const token = localStorage.getItem("pp_token");
    localStorage.removeItem("pp_token");
    localStorage.removeItem("pp_expired");
    localStorage.removeItem("pp_expired_notice_shown");
    localStorage.removeItem("pp_session_ended");
    expiryRef.current = null;
    setRemainingMs(null);
    setUser(null);

    if (token) {
      try {
        await api.post("/auth/logout");
      } catch (error) {
        console.error("Logout request failed:", error);
      }
    }

    window.location.assign("/account");
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      loginWithKey,
      logout,
      logoutToAccount,
      remainingMs,
      expiresAt: user?.expires_at || null,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, remainingMs]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
